import { MeRepository } from 'common/repositories/index';
import { mixpanelLogger as log } from 'common/services/logging/loggers';
import OSBrowserDetection from 'common/services/util/-RFC.OSBrowserDetection';
import { Session, SessionKeys } from 'common/services/-RFC.session.js';
import Util from 'common/services/util/-RFC.util.js';

let _events = [];

const _commonProperties = {
    Language: CWCONFIG.language,
    OS: window.navigator.platform,
    'OS Detailed': OSBrowserDetection.getOSVersion(),
    'Browser Version': window.browserName + window.browserVersion,
    'Device Size': `${window.screen.width}x${window.screen.height}`,
    'Browser Size': `${window.innerWidth}x${window.innerHeight}`,
    'User Type': 'unregistered', // if no anonymous jwt token
    'Referral Code': 'None',
    'Redirect Client': 'None', // SSO thirdParty login
    'Campaign Name': 'None',
    'Content Id': 'None',
    'Redirect URI': 'None',
    Licenses: '',
    'Licenses Names': '',
    'Runtimes Remaining': '',
    'Runtimes Remaining Min': '',
    'Runtimes Remaining Max': '',
    'Subscription Statuses': '',
    'Subscription Types': '',
    'Valid Payment Method': '',
    'Subscription Runtimes': '',
    Theme: localStorage.getItem('theme').indexOf('dark') > -1 ? 'Dark' : 'Light',
};

// observable for MeRepository
let meSubscriber;
const meObservable = MeRepository.getObservable();
const onNextMe = (user) => {

    if (user) {

        _commonProperties['User Type'] = Util.isAnonymous() ? 'anonymous' : 'registered';

        /* unsubscribe meObservable when the user is not anonymous anymore */
        if (user.data.attributes.email) {

            meSubscriber.dispose();

        }

    }

};
meSubscriber = meObservable.subscribe(onNextMe, () => { }, () => { });

Session.Observable()
    .from(SessionKeys.ReferralCode)
    .subscribe(
        () => {

            _commonProperties['Referral Code'] = Session.getValue(SessionKeys.ReferralCode);

        },
        angular.noop,
        angular.noop,
    );

Session.Observable()
    .from(SessionKeys.SsoExtraUrlParams)
    .subscribe(
        () => {

            // SSO set the client_id, redirect_uri and campaign params to mixpanel common
            const ssoParams = sessionStorage.getItem('SsoExtraUrlParams');
            if (ssoParams) {

                const parsedSsoParams = JSON.parse(ssoParams);

                _commonProperties['Redirect Client'] = parsedSsoParams.client_id || 'None';
                _commonProperties['Campaign Name'] = parsedSsoParams.campaign || 'None';
                _commonProperties['Content Id'] = parsedSsoParams.content_id || 'None';
                _commonProperties['Campaign Type'] = parsedSsoParams.campaign_type || 'None';
                _commonProperties['Redirect URI'] = parsedSsoParams.redirect_uri || 'None';

                if (_commonProperties['Redirect URI'] !== 'None') {

                    const redirectParams = _commonProperties['Redirect URI'].split('/');

                    if (redirectParams.includes('identity-assistant') && redirectParams.includes('ltb') && redirectParams.includes('notifications')) {

                        redirectParams.splice(redirectParams.indexOf('ltb') - 1, 1);
                        const redirectNotificationUrl = redirectParams.join('/');
                        _commonProperties['Redirect URI'] = redirectNotificationUrl;

                    }

                }

                if (parsedSsoParams.cw_ref_code) {

                    _commonProperties['Referral Code'] = parsedSsoParams.cw_ref_code;

                } else if (parsedSsoParams.redirect_uri && parsedSsoParams.redirect_uri.indexOf('cw_ref_code') > -1) {

                    const redirectUri = parsedSsoParams.redirect_uri;
                    const params = redirectUri.split('?');

                    if (params.length > 0) {

                        const refCodeIndex = params.findIndex(param => param.indexOf('cw_ref_code') > -1);
                        if (refCodeIndex) {

                            let splittedParams = params[refCodeIndex].split('=');
                            [, splittedParams] = splittedParams;
                            _commonProperties['Referral Code'] = splittedParams;

                        }

                    }

                }

            }

            // retrieve the x-a-* params and add them to the mixpanel common properties
            const queryParams = Session.getValue(SessionKeys.SsoExtraUrlParams);
            Object.keys(queryParams).forEach((key) => {

                if (key.indexOf('x-a-') > -1) {

                    _commonProperties[key] = queryParams[key];

                }

            });

        },
        angular.noop,
        angular.noop,
    );

/**
 * @ngdoc service
 * @name services.Mixpanel
 *
 * @description
 * This class is a wrapper over mixpanel library.
 */
const Mixpanel = {

    /**
     * @ngdoc function
     * @name services.Mixpanel#send
     * @methodOf services.Mixpanel
     * @function
     *
     * @description
     * This method is used to send mixapanel events
     *
     * @param {string} eventName The name of the event. This can be anything
     * @param {object} properties A set of properties to include with the event you're sending
     */
    send(eventName, properties) {

        const payloadMixpanel = {};
        angular.extend(payloadMixpanel, _commonProperties);

        if (angular.isObject(properties)) {

            angular.extend(payloadMixpanel, properties);

        }

        _events.push(eventName);

        log.info({
            type: eventName,
            data: payloadMixpanel,
        });

        // disable the event tracking for the internal test users eg. test@oemail.avira.com
        const user = MeRepository.getMyUser();
        if (user && user.attributes.email && user.attributes.email.endsWith('.avira.com')) {

            return null;

        }
        return window.mixpanel.track(eventName, payloadMixpanel);

    },

    /**
     * @ngdoc function
     * @name services.Mixpanel#getEvents
     * @methodOf services.Mixpanel
     * @function
     *
     * @description
     * This method returns event list
     *
     * @returns {array} list of events
     */
    getEvents() {

        return _events;

    },

    /**
    * @ngdoc function
    * @name services.Mixpanel#clearEvents
    * @methodOf services.Mixpanel
    * @function
    *
    * @description
    * This method remove all events from list
    */
    clearEvents() {

        _events = [];

    },

    /**
    * @ngdoc function
    * @name services.Mixpanel#clearCommonProperty
    * @methodOf services.Mixpanel
    * @function
    *
    * @description
    * This method removes a property from the default Mixpanel properties
    */
    clearCommonProperty(property) {

        delete _commonProperties[property];

    },

    /**
    * @ngdoc function
    * @name services.Mixpanel#setCommonProperty
    * @methodOf services.Mixpanel
    * @function
    *
    * @description
    * This method sets the value of a property from the default Mixpanel properties
    */
    setCommonProperty(property, value) {

        _commonProperties[property] = value;

    },

};

export default Mixpanel;



// WEBPACK FOOTER //
// ./scripts/common/services/stats/-RFC.Mixpanel.js